/* eslint-disable import/no-cycle */
import { nestCall, NestApiType } from '@/composables/nestApi';
import { UserState } from '@/composables/User';

/**
 * Lifecycle states of the V4 → V5 platform migration, mirrored on the
 * backend `platform_migration_v5_pending` key. 9-state funnel; some
 * states are owned by the external ZF2 stack (the V5 frontend only ever
 * reacts to the ones below where it redirects / waits):
 *
 *   none                         ⇢ not in the funnel. Router lets through.
 *   need_first_migration         ⇢ '-2' ZF2 to run initial migration.
 *   first_migration_in_progress  ⇢ '-1.5' ZF2 running initial migration.
 *   can_invite                   ⇢ '-1' eligible, admin will invite.
 *   invited                      ⇢ '0' V4 modal shows. No V5 redirect.
 *   in_progress                  ⇢ '1' user opted in — V5 wizard step 1
 *                                  (connect). On module connect → '2'.
 *   ready_to_migrate_data        ⇢ '2' module connected; waiting for ZF2
 *                                  to pick up the data migration.
 *   data_migration_in_progress   ⇢ '2.5' ZF2 migrating the data.
 *   data_migrated                ⇢ '3' ZF2 done — wait step calls /finalize.
 *   finalized                    ⇢ '4' on V5 platform — success + reload.
 *
 * The wait screen keeps the user on `/migration-v5/` for `in_progress`,
 * `ready_to_migrate_data`, `data_migration_in_progress` and
 * `data_migrated` (cf. `checkPlatformMigrationV5`).
 */
export type MigrationV5Status =
  | 'none'
  | 'need_first_migration'
  | 'first_migration_in_progress'
  | 'can_invite'
  | 'invited'
  | 'in_progress'
  | 'ready_to_migrate_data'
  | 'data_migration_in_progress'
  | 'data_migrated'
  | 'finalized';

export interface MigrationV5StatusResponse {
  idShop: number;
  status: MigrationV5Status;
}

/**
 * Statuts pour lesquels le shop est SERVI PAR LA PLATEFORME V5 — migration
 * active OU finalisée. Miroir front du backend `isV5ServedMigrationValue`
 * (`platform_migration_v5_pending` ∈ {'1','2','2.5','3','4'}). Les filets
 * anti-V4 (userGuard, redirectIfV4Shop) ne doivent JAMAIS renvoyer un de ces
 * shops vers V4. `none` / `invited` / états ZF2 négatifs ⇒ non servi (V4).
 *
 * ⚠️ À ne pas confondre avec `checkPlatformMigrationV5` (configs/complete) qui
 * EXCLUT `finalized` : celui-là répond à « faut-il PARQUER sur le wizard ? »,
 * alors qu'ici on répond à « le shop appartient-il à la plateforme V5 ? ».
 */
const V5_SERVED_STATUSES: MigrationV5Status[] = [
  'in_progress',
  'ready_to_migrate_data',
  'data_migration_in_progress',
  'data_migrated',
  'finalized',
];

export function isServedByV5(status: MigrationV5Status): boolean {
  return V5_SERVED_STATUSES.includes(status);
}

const activeShopId = (): number => UserState.activeShop?.id ?? 0;

/**
 * Read-only status check. Used by:
 *   - the router guard (`checkPlatformMigrationV5`) once per page load
 *   - the waiting step polling loop (every 10 s)
 *
 * Fails open: any error returns `'none'` so a transient API hiccup
 * doesn't trap the user on the migration page indefinitely.
 */
export const fetchMigrationV5Status = async (
  shopId?: number,
): Promise<MigrationV5Status> => {
  const id = shopId ?? activeShopId();
  if (!id) return 'none';
  try {
    const res: MigrationV5StatusResponse | undefined = await nestCall(
      'get',
      NestApiType.DEFAULT,
      `/migration-v5/${id}/status`,
    );
    return res?.status ?? 'none';
  } catch (e) {
    return 'none';
  }
};

/**
 * Triggered when the shop owner clicks "Migrate now" on the V4 UI.
 * Backend transitions `invited` → `in_progress`. On success the caller
 * should navigate (or reload) so the router guard picks up the new
 * state and routes to /migration-v5/.
 *
 * Backend rejects if the shop is not in `invited` (defensive against
 * skipping the admin invite step) — surface the error so the UI can
 * show a meaningful message.
 */
export const startMigrationV5 = async (
  shopId?: number,
): Promise<MigrationV5StatusResponse> => {
  const id = shopId ?? activeShopId();
  if (!id) throw new Error('No active shop');
  return nestCall(
    'post',
    NestApiType.DEFAULT,
    `/migration-v5/${id}/start`,
  ) as Promise<MigrationV5StatusResponse>;
};

/**
 * Signals to the backend that step 1 (module connection) is done.
 * Triggers the Telegram alert to the admin team — idempotent
 * server-side, safe to call again if the user reloads the wait screen.
 */
export const notifyMigrationV5Ready = async (
  shopId?: number,
): Promise<MigrationV5StatusResponse> => {
  const id = shopId ?? activeShopId();
  if (!id) throw new Error('No active shop');
  return nestCall(
    'post',
    NestApiType.DEFAULT,
    `/migration-v5/${id}/ready`,
  ) as Promise<MigrationV5StatusResponse>;
};

/**
 * Runs the V5 seeders (default data source, system API key, VAPID,
 * default lists, V5 IDs cache) and flips the backend state from
 * `data_migrated` to `finalized`. Called automatically by the wait step
 * the first time it polls and sees `data_migrated`.
 *
 * On HTTP error: the caller (wait step) should NOT throw — keep
 * polling, the backend is itself idempotent so the next /finalize call
 * will retry the seeders without side-effects. A persistent error
 * means the shop stays at `data_migrated` and an admin needs to
 * intervene (logs are on `alert_payments` Telegram channel).
 */
export const finalizeMigrationV5 = async (
  shopId?: number,
): Promise<MigrationV5StatusResponse> => {
  const id = shopId ?? activeShopId();
  if (!id) throw new Error('No active shop');
  return nestCall(
    'post',
    NestApiType.DEFAULT,
    `/migration-v5/${id}/finalize`,
  ) as Promise<MigrationV5StatusResponse>;
};
